import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const STEP_STORAGE_KEY = 'tutorial_current_step';

export const useTutorialState = (userId: string | undefined) => {
  const [shouldShowTutorial, setShouldShowTutorial] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    checkTutorialStatus();
  }, [userId]);

  const checkTutorialStatus = async () => {
    if (!userId) return;

    try {
      // Check Supabase first
      const { data, error } = await supabase
        .from('user_tutorial_progress')
        .select('tutorial_completed')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking tutorial status:', error);
      }

      // If no record exists in Supabase, check localStorage
      if (!data) {
        const localProgress = localStorage.getItem(`tutorial_completed_${userId}`);
        setShouldShowTutorial(!localProgress);
      } else {
        setShouldShowTutorial(!data.tutorial_completed);
      }
    } catch (error) {
      console.error('Error in checkTutorialStatus:', error);
      // Fallback to localStorage
      const localProgress = localStorage.getItem(`tutorial_completed_${userId}`);
      setShouldShowTutorial(!localProgress);
    } finally {
      setIsLoading(false);
    }
  };

  const markTutorialComplete = async (cancelled: boolean = false) => {
    if (!userId || isMarkingComplete || isCancelling) return;

    try {
      if (cancelled) {
        setIsCancelling(true);
      } else {
        setIsMarkingComplete(true);
      }
      
      // 1. Update state immediately for instant UI feedback
      setShouldShowTutorial(false);
      
      // 2. Save to localStorage FIRST (guaranteed immediate persistence)
      localStorage.setItem(`tutorial_completed_${userId}`, cancelled ? 'cancelled' : 'completed');
      console.log(`[Tutorial] ✅ Saved to localStorage (${cancelled ? 'cancelled' : 'completed'})`);

      // 3. Save to Supabase with retry logic
      let retries = 3;
      let success = false;
      
      while (retries > 0 && !success) {
        try {
          const { error } = await supabase
            .from('user_tutorial_progress')
            .upsert({
              user_id: userId,
              tutorial_completed: true,
              completed_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });

          if (error) {
            console.error(`[Tutorial] ⚠️ Supabase save attempt failed (${4 - retries}/3):`, error);
            retries--;
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
            }
          } else {
            success = true;
            console.log(`[Tutorial] ✅ Successfully saved to Supabase (${cancelled ? 'cancelled' : 'completed'})`);
          }
        } catch (err) {
          console.error(`[Tutorial] ⚠️ Supabase save error (${4 - retries}/3):`, err);
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      if (!success) {
        console.warn('[Tutorial] ⚠️ Failed to save to Supabase after 3 attempts, but localStorage is saved');
      }

      // 4. Verificar status novamente após salvar
      await checkTutorialStatus();
      console.log('[Tutorial] ✅ Status rechecked after completion/cancellation');

    } catch (error) {
      console.error('[Tutorial] ❌ Critical error in markTutorialComplete:', error);
      // Even if everything fails, state is already updated
    } finally {
      setIsMarkingComplete(false);
      setIsCancelling(false);
    }
  };

  const resetTutorial = async () => {
    if (!userId) return;

    try {
      // Remove from localStorage (both completion status and current step)
      localStorage.removeItem(`tutorial_completed_${userId}`);
      localStorage.removeItem(STEP_STORAGE_KEY);
      
      // Reset in Supabase - update to reset state instead of delete
      const { error } = await supabase
        .from('user_tutorial_progress')
        .upsert({
          user_id: userId,
          tutorial_completed: false,
          current_step: 0,
          completed_at: null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

      if (error) {
        console.error('Error resetting tutorial in database:', error);
        // Still allow the tutorial to show
      }

      setShouldShowTutorial(true);
      
      toast({
        title: "Tutorial reiniciado",
        description: "O tutorial será exibido novamente.",
      });
    } catch (error) {
      console.error('Error in resetTutorial:', error);
      toast({
        title: "Erro ao reiniciar tutorial",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    }
  };

  const cancelTutorial = async () => {
    await markTutorialComplete(true);
  };

  return {
    shouldShowTutorial,
    isLoading,
    isMarkingComplete,
    isCancelling,
    markTutorialComplete,
    cancelTutorial,
    resetTutorial,
  };
};
